import { useEffect, useMemo, useState } from "react";
import { api, reportExportUrl } from "@/lib/api";
import { BRANDS, branchesFor, money } from "@/lib/brands";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart3, Download, FileSpreadsheet, FileText } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid } from "recharts";

const PIE = ["#2C523E", "#CF856B", "#D9B97F", "#718C83", "#A68E88"];
const BAR = "#2C523E";
const LINE = "#CF856B";
const ALL = "all";

const Kpi = ({ label, value, testid }) => (
  <Card className="p-5 border-border/60" data-testid={testid}>
    <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-2xl font-bold font-primary mt-1">{value}</div>
  </Card>
);

export default function BusinessReports() {
  const [f, setF] = useState({ start: "", end: "", brand: ALL, branch: ALL, service_line: ALL, staff: ALL });
  const [staffUsers, setStaffUsers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/users").then(({ data }) => setStaffUsers(data.filter((u) => u.role !== "admin"))); }, []);

  const params = useMemo(() => {
    const p = {};
    Object.entries(f).forEach(([k, v]) => { if (v && v !== ALL) p[k] = v; });
    return p;
  }, [f]);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/reports", { params }); setData(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [params]);

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v, ...(k === "brand" ? { branch: ALL } : {}) }));

  const bar = (arr) => (arr || []).map((x) => ({ name: x.label, value: x.value }));

  return (
    <div className="p-4 md:p-8 max-w-[1300px] mx-auto animate-fade-in">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2"><BarChart3 className="h-6 w-6 text-primary" /><h1 className="font-primary text-3xl md:text-4xl font-bold tracking-tight">Business Reports</h1></div>
          <p className="text-sm text-muted-foreground mt-1">Consolidated across Consultations, Grooming &amp; Boarding. Brands reported separately.</p>
        </div>
        <div className="flex gap-2">
          <a href={reportExportUrl("pdf", params)} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="export-pdf-btn"><FileText className="h-4 w-4 mr-2" />PDF</Button></a>
          <a href={reportExportUrl("xlsx", params)} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="export-xlsx-btn"><FileSpreadsheet className="h-4 w-4 mr-2" />Excel</Button></a>
          <a href={reportExportUrl("csv", params)} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="export-csv-btn"><Download className="h-4 w-4 mr-2" />CSV</Button></a>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 border-border/60 mb-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" value={f.start} onChange={(e) => set("start")(e.target.value)} data-testid="report-start" /></div>
          <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" value={f.end} onChange={(e) => set("end")(e.target.value)} data-testid="report-end" /></div>
          <div className="space-y-1"><Label className="text-xs">Store / Brand</Label>
            <Select value={f.brand} onValueChange={set("brand")}><SelectTrigger data-testid="report-brand"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>All brands</SelectItem>{BRANDS.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Branch</Label>
            <Select value={f.branch} onValueChange={set("branch")} disabled={f.brand === ALL}><SelectTrigger data-testid="report-branch"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>All branches</SelectItem>{branchesFor(f.brand).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Service line</Label>
            <Select value={f.service_line} onValueChange={set("service_line")}><SelectTrigger data-testid="report-service-line"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>All services</SelectItem><SelectItem value="consultation">Consultations</SelectItem><SelectItem value="grooming">Grooming</SelectItem><SelectItem value="boarding">Boarding</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Staff / Doctor</Label>
            <Select value={f.staff} onValueChange={set("staff")}><SelectTrigger data-testid="report-staff"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={ALL}>Everyone</SelectItem>{staffUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {loading || !data ? <div className="p-8 text-muted-foreground">Loading…</div> : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Kpi label="Total Revenue" value={money(data.total_revenue)} testid="kpi-revenue" />
            <Kpi label="Paid Transactions" value={data.transaction_count} testid="kpi-txns" />
            <Kpi label="Consultations" value={data.consultation_volume} testid="kpi-consults" />
            <Kpi label="New Customers" value={data.new_customers} testid="kpi-new" />
            <Kpi label="Returning" value={data.returning_customers} testid="kpi-returning" />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="p-5 border-border/60">
              <h3 className="font-primary font-semibold mb-4">Revenue by Branch</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={bar(data.revenue_by_branch)}><CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip formatter={(v) => money(v)} /><Bar dataKey="value" fill={BAR} radius={[4, 4, 0, 0]} /></BarChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-5 border-border/60">
              <h3 className="font-primary font-semibold mb-4">Revenue by Service Line</h3>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart><Pie data={bar(data.revenue_by_service_line)} dataKey="value" nameKey="name" outerRadius={90} label={(e) => e.name}>{bar(data.revenue_by_service_line).map((entry, i) => <Cell key={entry.name} fill={PIE[i % PIE.length]} />)}</Pie><Tooltip formatter={(v) => money(v)} /></PieChart>
              </ResponsiveContainer>
            </Card>
          </div>

          <Card className="p-5 border-border/60">
            <h3 className="font-primary font-semibold mb-4">Monthly Revenue Trend</h3>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={bar(data.revenue_trend)}><CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip formatter={(v) => money(v)} /><Line type="monotone" dataKey="value" stroke={LINE} strokeWidth={2} dot={{ r: 3 }} /></LineChart>
            </ResponsiveContainer>
          </Card>

          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="p-5 border-border/60">
              <h3 className="font-primary font-semibold mb-4">Grooming Service Mix</h3>
              {data.grooming_service_counts.length === 0 ? <p className="text-sm text-muted-foreground">No grooming services in range.</p> :
                <ResponsiveContainer width="100%" height={Math.max(180, data.grooming_service_counts.length * 26)}>
                  <BarChart layout="vertical" data={bar(data.grooming_service_counts)}><XAxis type="number" tick={{ fontSize: 10 }} /><YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 9 }} /><Tooltip /><Bar dataKey="value" fill={BAR} radius={[0, 4, 4, 0]} /></BarChart>
                </ResponsiveContainer>}
            </Card>
            <Card className="p-5 border-border/60">
              <h3 className="font-primary font-semibold mb-4">Operational Snapshot</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border border-border/60 p-3"><div className="text-muted-foreground text-xs">Day Boarding</div><div className="text-xl font-semibold">{data.boarding_occupancy.day}</div></div>
                <div className="rounded-lg border border-border/60 p-3"><div className="text-muted-foreground text-xs">Overnight Boarding</div><div className="text-xl font-semibold">{data.boarding_occupancy.overnight}</div></div>
                <div className="rounded-lg border border-border/60 p-3"><div className="text-muted-foreground text-xs">Pickups</div><div className="text-xl font-semibold">{data.pickup_dropoff.pickup}</div></div>
                <div className="rounded-lg border border-border/60 p-3"><div className="text-muted-foreground text-xs">Drop-offs</div><div className="text-xl font-semibold">{data.pickup_dropoff.dropoff}</div></div>
              </div>
              <h4 className="font-medium mt-4 mb-2 text-sm">Health flags at intake</h4>
              <div className="flex flex-wrap gap-2">
                {data.health_flags.map((h) => <span key={h.label} className="text-xs px-2 py-1 rounded-full bg-muted">{h.label}: <b>{h.value}</b></span>)}
              </div>
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="p-5 border-border/60" data-testid="top-customers">
              <h3 className="font-primary font-semibold mb-3">Top Customers</h3>
              <div className="divide-y divide-border">
                {data.top_customers.length === 0 && <p className="text-sm text-muted-foreground">No data.</p>}
                {data.top_customers.map((c) => (
                  <div key={c.patient_id} className="flex justify-between py-2 text-sm"><span>{c.pet_name} <span className="text-muted-foreground">· {c.owner_name} · {c.brand}</span></span><span className="font-semibold">{money(c.total_spend)}</span></div>
                ))}
              </div>
            </Card>
            <Card className="p-5 border-border/60" data-testid="staff-activity">
              <h3 className="font-primary font-semibold mb-3">Staff / Doctor Activity</h3>
              <div className="divide-y divide-border">
                {data.staff_activity.length === 0 && <p className="text-sm text-muted-foreground">No data.</p>}
                {data.staff_activity.map((s, i) => (
                  <div key={s.name || i} className="flex justify-between py-2 text-sm"><span>{s.name} <span className="text-muted-foreground capitalize">· {s.role}</span></span><span>{s.bookings} bookings · <b>{money(s.revenue)}</b></span></div>
                ))}
              </div>
            </Card>
          </div>

          <Card className="p-5 border-border/60">
            <h3 className="font-primary font-semibold mb-3">Booking Status</h3>
            <div className="grid sm:grid-cols-3 gap-3 text-sm">
              {Object.entries(data.booking_status).map(([line, s]) => (
                <div key={line} className="rounded-lg border border-border/60 p-3">
                  <div className="font-medium">{line}</div>
                  <div className="text-muted-foreground text-xs mt-1">Open: {s.open} · Closed: {s.closed} · Edited after close: {s.edited_after_close}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
